import { useCallback } from 'react';
import { useStoreon } from 'storeon/react';
import {
  useGetSponsoredProducts,
  useGetSponsoredBrands,
  useSearchbarLogic,
  useSponsoredProductsPlacementConfig,
  useSponsoredBrandsPlacementConfig,
} from 'components/header/hooks';
import { State } from 'store/types';
import { getCustomerId } from 'selectors';
import type { ProductRedirectionStore, SuggestionsStore } from 'reducers';
import { createCookies, getLangFromCookies } from 'utils';
import { getRegionId, getRegionName } from './utils';

export type UseSearchBarLogicReturnType = {
  searchSuggestions: SuggestionsStore['suggestions'];
  getSuggestions: (value: string) => void;
  resetServices: () => void;
  productRedirection: ProductRedirectionStore['productRedirection'];
  resetSponsoredProducts: () => void;
  resetSponsoredBrands: () => void;
};

/**
 * @description обёртка над хуком useSearchbarLogic для Header
 * @returns { UseSearchBarLogicReturnType }
 */
export const useSearchBarLogic = (): UseSearchBarLogicReturnType => {
  const { dispatch, suggestions, cookies, model, productRedirection } = useStoreon<State>(
    'cookies',
    'suggestions',
    'productRedirection',
    'model',
  );

  const { getSuggestions, resetServices } = useSearchbarLogic(dispatch);
  const { getSponsoredProducts, resetSponsoredProducts } = useGetSponsoredProducts(dispatch);
  const sponsoredProductConfig = useSponsoredProductsPlacementConfig();
  const sponsoredBrandConfig = useSponsoredBrandsPlacementConfig();
  const { getSponsoredBrands, resetSponsoredBrands } = useGetSponsoredBrands(dispatch);

  const getSuggestionsSafe = useCallback(
    (value: string) => {
      const regionId = getRegionId(model?.properties);
      const regionName = getRegionName(regionId, model?.regions);
      const customerId = getCustomerId(cookies);
      const lang = getLangFromCookies(createCookies(cookies));

      if (regionId) {
        getSuggestions({
          query: value,
          region: regionId,
          customerId,
          lang,
        });
      }

      if (regionId && regionName && value) {
        if (sponsoredProductConfig) {
          const { session, link, placementId } = sponsoredProductConfig;
          // запрос спонсорских товаров
          getSponsoredProducts({
            searchQuery: value,
            regionId,
            regionName,
            ...(session && { session }),
            ...(!session && link && { 'link.name': link.name, 'link.value': link.value }),
            placementId,
          });
        }
        if (sponsoredBrandConfig) {
          const { session, link, placementId } = sponsoredBrandConfig;
          // запрос спонсорских брендов
          getSponsoredBrands({
            searchQuery: value,
            regionId,
            regionName,
            ...(session && { session }),
            ...(!session && link && { 'link.name': link.name, 'link.value': link.value }),
            placementId,
          });
        }
      }
    },
    [
      model?.properties,
      model?.regions,
      cookies,
      sponsoredProductConfig,
      sponsoredBrandConfig,
      getSuggestions,
      getSponsoredProducts,
      getSponsoredBrands,
    ],
  );

  return {
    searchSuggestions: suggestions,
    getSuggestions: getSuggestionsSafe,
    resetServices,
    productRedirection,
    resetSponsoredProducts,
    resetSponsoredBrands,
  };
};
